import React, { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const STAGES = ["Prospect", "Contacted", "Interested", "Proposal Sent", "Negotiation", "Confirmed", "Lost"];
const CATS = ["Sportswear", "Sports Equipment", "Food & Beverage", "Fitness", "Healthcare", "Real Estate", "Local Business", "Education", "Lifestyle", "Other"];

export default function Sponsorships() {
  const [sponsors, setSponsors] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    brand: "", category: "Sportswear", contact_name: "", phone: "", email: "",
    assigned_to: "", event_id: "", sponsorship_ask: "", expected_value: 0, final_value: 0,
    status: "Prospect", notes: "",
  });

  const load = async () => {
    const [s, e, u] = await Promise.all([api.get("/sponsors"), api.get("/events"), api.get("/users")]);
    setSponsors(s.data); setEvents(e.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.brand.trim()) return toast.error("Brand name required");
    const ev = events.find((x) => x.id === f.event_id);
    const a = users.find((u) => u.id === f.assigned_to);
    try {
      await api.post("/sponsors", { ...f, event_name: ev?.name || null, assigned_to_name: a?.name || "" });
      toast.success("Sponsor added"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const move = async (s, stage) => { await api.patch(`/sponsors/${s.id}`, { status: stage }); load(); };

  const totalPipeline = sponsors.filter((s) => !["Confirmed", "Lost"].includes(s.status))
    .reduce((sum, s) => sum + Number(s.expected_value || 0), 0);
  const confirmed = sponsors.filter((s) => s.status === "Confirmed")
    .reduce((sum, s) => sum + Number(s.final_value || 0), 0);

  return (
    <div className="space-y-6" data-testid="sponsorships-page">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Sponsorships</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Sponsorship Pipeline</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-sponsor-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New Sponsor</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New Sponsor</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Brand</Label><Input data-testid="sponsor-brand-input" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} /></div>
              <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Status</Label><Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Contact</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              <div><Label>Event</Label><Select value={f.event_id} onValueChange={(v) => setF({ ...f, event_id: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Assigned to</Label><Select value={f.assigned_to} onValueChange={(v) => setF({ ...f, assigned_to: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Sponsorship Ask</Label><Input value={f.sponsorship_ask} onChange={(e) => setF({ ...f, sponsorship_ask: e.target.value })} /></div>
              <div><Label>Expected Value (₹)</Label><Input type="number" value={f.expected_value} onChange={(e) => setF({ ...f, expected_value: +e.target.value })} /></div>
              <div><Label>Final Value (₹)</Label><Input type="number" value={f.final_value} onChange={(e) => setF({ ...f, final_value: +e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="sponsor-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total Pipeline</div><div className="text-2xl font-black mt-1">{fmtINR(totalPipeline)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Confirmed</div><div className="text-2xl font-black mt-1 text-emerald-700">{fmtINR(confirmed)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Active Prospects</div><div className="text-2xl font-black mt-1">{sponsors.filter((s) => !["Confirmed", "Lost"].includes(s.status)).length}</div></CardContent></Card>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => (
          <div key={s} className="min-w-[260px] flex-1 bg-slate-100 rounded-md p-3">
            <div className="flex justify-between mb-3"><div className="text-xs uppercase tracking-widest text-slate-600 font-bold">{s}</div><span className="text-xs text-slate-500">{sponsors.filter((x) => x.status === s).length}</span></div>
            <div className="space-y-2">
              {sponsors.filter((x) => x.status === s).map((sp) => (
                <Card key={sp.id} data-testid={`sponsor-card-${sp.id}`} className="border-slate-200 bg-white">
                  <CardContent className="p-3">
                    <div className="font-semibold text-sm">{sp.brand}</div>
                    <div className="text-xs text-slate-500">{sp.category} · {sp.event_name || "—"}</div>
                    <div className="text-xs font-mono mt-1">{fmtINR(sp.expected_value)}</div>
                    <Select value={sp.status} onValueChange={(v) => move(sp, v)}>
                      <SelectTrigger className="h-6 text-xs mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
